import { Building2, TrendingUp, Factory, Briefcase, Globe2, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurCustomers = () => {
  const segments = [
    {
      icon: <Factory className="w-10 h-10 text-[#007BFF]" />,
      title: 'International Energy Companies',
      description: 'We support integrated energy companies, international oil companies, and global energy traders with intelligence on commodity markets, country analysis, competitive dynamics, and strategic themes.',
      services: [
        'Market fundamentals and price outlook',
        'Competitive intelligence',
        'Country risk assessment',
        'Strategic positioning analysis'
      ]
    },
    {
      icon: <Globe2 className="w-10 h-10 text-[#007BFF]" />,
      title: 'National Oil & Gas Companies',
      description: 'National energy companies rely on Enerlligence for independent market analysis, benchmarking intelligence, and strategic advisory support for investment planning and operational optimization.',
      services: [
        'Independent market perspectives',
        'Benchmarking and performance analysis',
        'Investment opportunity assessment',
        'Policy and regulatory analysis'
      ]
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-[#007BFF]" />,
      title: 'Renewable Energy Developers',
      description: 'Renewable energy companies access our intelligence on power markets, policy frameworks, technology economics, and investment trends to inform development strategies and financing decisions.',
      services: [
        'Power market analysis',
        'Policy and incentive tracking',
        'Technology cost analysis',
        'Market entry strategies'
      ]
    },
    {
      icon: <Building2 className="w-10 h-10 text-[#007BFF]" />,
      title: 'Financial Institutions',
      description: 'Banks, investment firms, and private equity organizations use our research for due diligence, portfolio analysis, risk assessment, and identifying investment opportunities across energy sectors.',
      services: [
        'Due diligence support',
        'Market opportunity identification',
        'Risk assessment',
        'Valuation analysis'
      ]
    },
    {
      icon: <Briefcase className="w-10 h-10 text-[#007BFF]" />,
      title: 'Advisory & Consulting Firms',
      description: 'Strategic advisory firms, management consultancies, and specialized energy consultants leverage Enerlligence intelligence to support client engagements and strategic recommendations.',
      services: [
        'Research support for client projects',
        'Market data and analysis',
        'Technical expertise',
        'Scenario modeling'
      ]
    },
    {
      icon: <FileText className="w-10 h-10 text-[#007BFF]" />,
      title: 'Government & Policy Institutions',
      description: 'Government agencies, regulatory bodies, and policy organizations access our analysis for energy policy development, regulatory design, and strategic planning in energy transitions.',
      services: [
        'Policy impact analysis',
        'Market design support',
        'Energy transition planning',
        'Regulatory framework assessment'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our Customers</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Trusted intelligence partner to leading organizations across the global energy ecosystem worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Customer Segments - 3x2 Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Who We Serve</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Enerlligence supports strategic decision-makers across energy companies, financial institutions, advisory firms, and policy organizations worldwide.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8">
            {segments.map((segment, index) => (
              <div key={index} className="bg-slate-50 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all duration-300 overflow-hidden flex flex-col h-full">
                <div className="p-8 flex flex-col flex-grow">
                  <div className="flex items-start space-x-3 mb-6">
                    {segment.icon}
                    <h3 className="text-2xl font-bold text-[#1F3C88]">{segment.title}</h3>
                  </div>
                  <p className="text-base text-slate-600 leading-relaxed mb-6 flex-grow">{segment.description}</p>
                  <div className="bg-white p-6 rounded-lg border border-slate-200 mt-auto">
                    <h4 className="text-base font-semibold text-[#1F3C88] mb-4">How We Support:</h4>
                    <ul className="space-y-2">
                      {segment.services.map((service, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Success */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-8">Client Success</h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-12">
            Our success is measured by the value we deliver to clients: intelligence that informs better strategic decisions, identifies market opportunities, and supports effective capital allocation.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <div className="text-4xl font-bold text-[#007BFF] mb-2">200+</div>
              <div className="text-base text-slate-600">Active Client Organizations</div>
            </div>
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <div className="text-4xl font-bold text-[#007BFF] mb-2">50+</div>
              <div className="text-base text-slate-600">Countries Represented</div>
            </div>
            <div className="bg-white p-8 rounded-lg border border-slate-200">
              <div className="text-4xl font-bold text-[#007BFF] mb-2">24/7</div>
              <div className="text-base text-slate-600">Intelligence Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Become a Client</h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-8">
            Discover how Enerlligence intelligence can support your strategic objectives
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurCustomers;
